import { useMemo } from "react";

import { startOfMonth, subMonths } from "date-fns";
import { useTranslation } from "react-i18next";

import type { Status } from "api/schemas/CareEventBilling";
import type { Professional } from "api/services/admin/schemas";

export const ALL_PROFESSIONALS = "all";
export const TOTAL_STATUS = "total";

export type ProfessionalValue = number | typeof ALL_PROFESSIONALS;
export type StatusValue = Status | typeof TOTAL_STATUS;

export const useFilterOptions = (professionals?: Professional[]) => {
  const { t } = useTranslation();

  const professionalOptions = useMemo(
    () => [
      { label: t("admin.all_professionals"), value: ALL_PROFESSIONALS },
      ...(professionals || []).map(professional => ({
        label: professional.name,
        value: professional.id,
      })),
    ],
    [professionals, t]
  );

  const monthOptions = useMemo(() => {
    const options: { label: string; value: string }[] = [];
    const today = new Date();

    const monthsConfig = [
      {
        label: t("admin.earnings.current_month"),
        value: 0,
      },
      {
        label: t("admin.earnings.last_month"),
        value: 1,
      },
      {
        label: t("admin.earnings.last_3_months"),
        value: 2,
      },
      {
        label: t("admin.earnings.last_6_months"),
        value: 5,
      },
      {
        label: t("admin.earnings.last_year"),
        value: 11,
      },
    ];

    monthsConfig.forEach(monthConfig => {
      const monthDate = subMonths(startOfMonth(today), monthConfig.value);
      options.push({
        label: monthConfig.label,
        value: new Date(Date.UTC(monthDate.getFullYear(), monthDate.getMonth(), 1)).toISOString(),
      });
    });

    return options;
  }, []);

  let defaultMonthValue = monthOptions[0].value;
  if (monthOptions.length > 1) {
    defaultMonthValue = monthOptions[monthOptions.length - 2].value;
  }

  const statusOptions = useMemo(() => {
    const options: { label: string; value: StatusValue }[] = [
      { label: t("admin.all_billing_statuses"), value: TOTAL_STATUS },
      { label: t("admin_billing.buttons.completed"), value: "completed" },
      { label: t("admin_billing.buttons.pending"), value: "pending" },
      { label: t("admin_billing.buttons.discarded"), value: "discarded" },
    ];
    return options;
  }, [t]);

  return {
    defaultMonthValue,
    monthOptions,
    statusOptions,
    professionalOptions,
  };
};
